import {
  Modal,
  Avatar,
  Input,
  Space,
  Button,
  message,
  Select,
  Tabs,
} from "antd";
import { useEffect, useState } from "react";
import {
  useAppliedInfluencersByBrand,
  useCampaignListByBrand,
  useInviteInfluencerToCampaign,
} from "../hooks/useCampaign";

export const InviteCampaign = ({
  visible,
  onClose,
  selectedInfluencers = [],
  mode,
}) => {
  const [inviteMessages, setInviteMessages] = useState({});
  const [campaignList, setCampaignList] = useState([]);
  const [page, setPage] = useState(1);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [messageMode, setMessageMode] = useState("all");
  const isSingleInvite = mode === "single";

  const { mutateAsync: inviteInfluencer } = useInviteInfluencerToCampaign();

  const campaignListByBrand = useCampaignListByBrand({
    page,
    status: "active",
    enabled: visible,
  });

  const { data: campaignData, isLoading: campaignLoading } =
    campaignListByBrand;

  const { data: appliedCampaignUser } = useAppliedInfluencersByBrand({
    campaignId: selectedCampaign,
    status: null,
    enabled: visible && !!selectedCampaign,
  });

  const appliedInfluencerIdSet = new Set(
    (appliedCampaignUser?.result ?? [])
      .map((item) => item.tiktok_influencer_id)
      .filter(Boolean)
  );
  const unavailableInfluencers = selectedInfluencers.filter((influencer) =>
    appliedInfluencerIdSet.has(influencer.id)
  );
  const availableInfluencers = selectedInfluencers.filter(
    (influencer) => !appliedInfluencerIdSet.has(influencer.id)
  );

  const total = campaignData?.total ?? 0;
  const isLastPage = campaignList.length >= total;

  useEffect(() => {
    if (!visible || !campaignData?.result) return;
    if (page === 1) {
      setCampaignList(campaignData.result);
    } else {
      setCampaignList((prev) => [...prev, ...campaignData.result]);
    }
  }, [visible, campaignData?.result, page]);

  const handleInviteMessageChange = (e, unique_id) => {
    setInviteMessages((prev) => ({
      ...prev,
      [unique_id]: e.target.value,
    }));
  };

  // 모달 열릴 때: 폼/선택 초기화. 닫힐 때: 리스트·페이지 초기화(다음에 열 때 깨끗하게)
  useEffect(() => {
    if (!visible) {
      setCampaignList([]);
      setPage(1);
      return;
    }

    setInviteMessages({});
    setSelectedCampaign(null);
    setPage(1);
  }, [visible]);

  const handleInvite = async () => {
    if (!selectedCampaign) {
      message.warning("캠페인을 선택해주세요.");
      return;
    }
    if (availableInfluencers.length === 0) {
      message.warning(
        "이미 신청된 크리에이터를 제외하면 초대할 대상이 없습니다."
      );
      return;
    }

    const allMessage = inviteMessages["all"] || "";

    // 백엔드 형식에 맞게 데이터 변환
    const users = availableInfluencers.map((influencer) => {
      const individualMessage = inviteMessages[influencer.unique_id];

      return {
        unique_id: influencer.unique_id,
        has_account: !!influencer.metrics,
        social_influencer_id: influencer.id || null,
        social: "tiktok",
        email: influencer.email || null,
        message: individualMessage || allMessage || "",
      };
    });

    const requestData = {
      campaign_id: selectedCampaign,
      message: allMessage,
      users,
    };

    const success = await inviteInfluencer(requestData);

    if (success) {
      onClose();
    }
  };

  const handleSelectChange = (value) => {
    setSelectedCampaign(value);
  };

  const renderInfluencerSummaryList = (list, type) => {
    if (list.length === 0) return null;

    const isAvailable = type === "available";
    const titleText = isAvailable
      ? "초대 가능한 크리에이터"
      : "참여 중인 크리에이터";

    return (
      <div className="rounded-lg border border-[#ededed] bg-[#fafafa] px-4 py-3">
        <div className="mb-2 text-[13px] font-semibold">
          {titleText} {list.length}명
        </div>
        <div className="flex flex-col gap-1.5">
          {list.map((influencer) => (
            <div
              key={influencer.unique_id}
              className="text-[12px] leading-relaxed text-[#171717]"
            >
              <span className="font-medium">{influencer.nickname || "-"}</span>{" "}
              <span className="text-gray-500">@{influencer.unique_id}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const tabItems = [
    {
      key: "all",
      label: `${isSingleInvite ? "메시지" : "전체 메시지"}`,
      children: (
        <div className="max-h-[500px] space-y-3 overflow-y-auto pr-1">
          {selectedCampaign ? (
            <>
              {renderInfluencerSummaryList(availableInfluencers, "available")}
              {renderInfluencerSummaryList(
                unavailableInfluencers,
                "unavailable"
              )}
            </>
          ) : null}
          <Input.TextArea
            rows={3}
            placeholder="모든 인플루언서에게 보낼 메시지"
            value={inviteMessages["all"] || ""}
            onChange={(e) => handleInviteMessageChange(e, "all")}
            disabled={!!selectedCampaign && availableInfluencers.length === 0}
            style={{ resize: "none" }}
          />
        </div>
      ),
    },
    ...(!isSingleInvite
      ? [
          {
            key: "individual",
            label: "개별 메시지",
            children: (
              <div className="max-h-[500px] overflow-y-auto pr-1">
                <Space
                  direction="vertical"
                  size="large"
                  className="w-full overflow-y-auto"
                >
                  {selectedInfluencers.map((influencer) => {
                    const isUnavailable = appliedInfluencerIdSet.has(
                      influencer.id
                    );
                    return (
                      <div
                        key={influencer.unique_id}
                        className={`border rounded-lg p-4 shadow-sm ${
                          isUnavailable
                            ? "bg-[#f5f5f5] border-[#d9d9d9] opacity-70"
                            : "bg-white"
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <Avatar
                            src={influencer.profile_img}
                            size={36}
                            style={{ border: "2px solid #F0F0F0" }}
                          />
                          <div>
                            <div className="font-semibold text-base">
                              {influencer.nickname || "-"}
                            </div>
                            <div className="text-sm text-gray-500">
                              @{influencer.unique_id}
                            </div>
                            {isUnavailable ? (
                              <div className="mt-1 text-[12px] font-medium text-[#d46b08]">
                                이미 신청된 크리에이터
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <Input.TextArea
                          rows={2}
                          placeholder={`${influencer.nickname}님에게 보낼 초대 메시지`}
                          value={inviteMessages[influencer.unique_id] || ""}
                          disabled={isUnavailable}
                          onChange={(e) =>
                            handleInviteMessageChange(e, influencer.unique_id)
                          }
                          style={{ resize: "none" }}
                        />
                      </div>
                    );
                  })}
                </Space>
              </div>
            ),
          },
        ]
      : []),
  ];
  return (
    <Modal
      title="캠페인 초대하기"
      open={visible}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="cancel" onClick={onClose}>
          취소
        </Button>,
        <Button key="invite" type="primary" onClick={handleInvite}>
          초대하기
        </Button>,
      ]}
    >
      <div>
        <div className="mt-4 mb-4">
          <p className="mb-2 font-medium">캠페인 선택</p>

          <Select
            placeholder="캠페인을 선택해주세요"
            value={selectedCampaign}
            onChange={handleSelectChange}
            className="w-[200px]"
            loading={campaignLoading}
            onPopupScroll={(e) => {
              const target = e.target;

              if (
                target.scrollTop + target.clientHeight >=
                target.scrollHeight - 8
              ) {
                if (!campaignLoading && !isLastPage) {
                  setPage((prev) => prev + 1);
                }
              }
            }}
          >
            {campaignList.map((campaign) => (
              <Select.Option key={campaign.id} value={campaign.id}>
                {campaign.title}
              </Select.Option>
            ))}
          </Select>
        </div>

        <p className="mt-8 font-medium">
          선택한 인플루언서: {selectedInfluencers.length}명
        </p>
        {selectedCampaign ? (
          <p className="mt-2 text-[13px] text-gray-500">
            이미 캠페인에 신청한 크리에이터는 초대 대상에서 자동 제외됩니다.
          </p>
        ) : null}

        {selectedInfluencers.length > 0 && (
          <div className="mt-4">
            <Tabs
              activeKey={messageMode}
              onChange={(key) => setMessageMode(key)}
              items={tabItems}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
